/**
 * `applyProposalVoteOptimistic` — optimistic-cache helper for the
 * `useSetProposalVote` / `useClearProposalVote` mutations.
 *
 * Patches the one active identity overlay (`myVote`) and the one canonical
 * proposal entity (`upvotes` / `downvotes` / `netVotes`). Filter/sort list
 * caches contain ids only and remain untouched.
 *
 * The counts are authoritative aggregates from the indexer; the indexer
 * re-broadcasts the post-vote totals on `morfi.market.{pubkey}.votes.v0` within
 * ~100 ms, and the `set:` realtime recipe (`recomputeNetVotes`) REPLACES these
 * optimistic values with the canonical totals. Because realtime uses `set`
 * (last-wins) rather than `add`, the optimistic bump can never double-count —
 * it is a placeholder the authoritative event overwrites.
 *
 * `vars.kind` discriminates the action:
 *   • `'up'` / `'down'` → set myVote to that kind (useSetProposalVote).
 *   • `undefined`       → clear myVote to null (useClearProposalVote — vars
 *     carries only `pubkey`, no `kind` field).
 *
 * `createMutation` computes every patch against the same pre-write snapshot,
 * so the entity always derives its delta from the overlay's prior vote.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { getActiveViewerScope, viewerOverlayQueryKey } from '../core/internal/viewer-overlay.js';

type MyVoteValue = 'up' | 'down' | null;

interface ProposalRowLike {
  readonly pubkey?: string;
  readonly myVote?: MyVoteValue;
  readonly upvotes?: number;
  readonly downvotes?: number;
  readonly netVotes?: number;
}

function nextMyVoteFrom(vars: Record<string, unknown>): MyVoteValue {
  const kind = vars.kind;
  if (kind === 'up' || kind === 'down') return kind;
  return null;
}

function voteCount(value: number | undefined): number {
  return typeof value === 'number' ? value : 0;
}

/**
 * Apply the prev→next transition to one row. Returns the same reference when
 * the row already holds `nextMyVote` (no-op) so structural sharing is kept.
 */
function patchRow<Row extends ProposalRowLike>(row: Row, nextMyVote: MyVoteValue): Row {
  const prevMyVote: MyVoteValue = row.myVote ?? null;
  if (prevMyVote === nextMyVote) return row;
  // Delta from removing the prior slot and adding the new one. Covers every
  // transition: null→up, up→null, up→down, etc.
  const upDelta = (nextMyVote === 'up' ? 1 : 0) - (prevMyVote === 'up' ? 1 : 0);
  const downDelta = (nextMyVote === 'down' ? 1 : 0) - (prevMyVote === 'down' ? 1 : 0);
  const upvotes = voteCount(row.upvotes) + upDelta;
  const downvotes = voteCount(row.downvotes) + downDelta;
  return { ...row, myVote: nextMyVote, upvotes, downvotes, netVotes: upvotes - downvotes };
}

export function applyProposalVoteOptimistic<T>(
  prev: T,
  vars: Record<string, unknown>,
  queryKey: QueryKey = [],
  queryClient?: QueryClient,
): T {
  if (prev === null || prev === undefined || typeof prev !== 'object') return prev;
  const pubkey = vars.pubkey;
  if (typeof pubkey !== 'string' || pubkey.length === 0) return prev;
  const nextMyVote = nextMyVoteFrom(vars);
  if (queryClient === undefined) return prev;

  const activeScope = getActiveViewerScope(queryClient);
  const entityKey = ['proposals', 'entity', pubkey] as const;
  const overlayKey = viewerOverlayQueryKey(
    (id) => ['proposals', 'entity', id],
    pubkey,
    'identity',
    activeScope,
  );
  if (sameKey(queryKey, overlayKey)) {
    const row = prev as ProposalRowLike;
    return row.myVote === nextMyVote ? prev : ({ ...row, myVote: nextMyVote } as T);
  }
  if (sameKey(queryKey, entityKey)) {
    const overlay = queryClient.getQueryData<ProposalRowLike>(overlayKey);
    if ((overlay?.myVote ?? null) === nextMyVote) return prev;
    const patched = patchRow(
      { ...(prev as ProposalRowLike), myVote: overlay?.myVote ?? null },
      nextMyVote,
    );
    const { myVote: _viewerVote, ...global } = patched;
    return global as T;
  }
  return prev;
}

function sameKey(left: QueryKey, right: QueryKey): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}
